import { Fragment, useEffect, useState } from 'react';
import Sidebar from './Sidebar';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { getUser, updateUser } from '../../actions/userActions';
import { clearError, clearUserUpdated } from '../../slices/userSlice';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, useNavigate } from 'react-router-dom';
import {
  faCartShopping,
  faFilter,
  faPencil,
  faSearch,
  faDashboard,
  faList,
  faShoppingBag,
  faSort,
  faUserPlus,
} from '@fortawesome/free-solid-svg-icons';
import { Dropdown } from 'react-bootstrap';
import Drawer from '@mui/material/Drawer';
import MetaData from '../layouts/MetaData';

import jsPDF from 'jspdf';

export default function UpdateUser() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [glocreMargin, setGlocreMargin] = useState(5);
  const [cancelledChequeFile, setCancelledChequeFile] = useState(null);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [deactivateRemarks, setDeactivateRemarks] = useState('');

  const { id: userId } = useParams();

  const { loading, isUserUpdated, error, user } = useSelector(
    state => state.userState
  );
  const { user: authUser } = useSelector(state => state.authState);

  const dispatch = useDispatch();

  const submitHandler = e => {
    e.preventDefault();
    const formData = new FormData();

    formData.append('name', name);
    formData.append('email', email);
    formData.append('role', role);
    formData.append('glocreMargin', glocreMargin);

    // Add this if user is seller and cheque upload is available
    if (role === 'seller' && cancelledChequeFile) {
      formData.append('cancelledCheque', cancelledChequeFile);
    }

    dispatch(updateUser(userId, formData));
  };

  const downloadPDF = async () => {
    try {
      const pdf = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'a4',
        compress: true,
      });

      const logo = '/images/logo.png';
      const pageWidth = 210;

      const margin = 20;
      const labelWidth = 55;
      const valueWidth = 115;
      const lineHeight = 6;

      let y = 30;

      // ===== HEADER =====

      // LEFT aligned logo (changed only X position)
      pdf.addImage(logo, 'PNG', 15, 5, 40, 10);

      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');

      pdf.setFontSize(14);
      pdf.text('User Details Report', pageWidth / 2, 21, { align: 'center' });

      // ===== ROW FUNCTION =====
      const row = (label, value) => {
        const text = String(value || '-');

        const lines = pdf.splitTextToSize(text, valueWidth - 6);

        const height = lines.length * lineHeight + 4;

        pdf.rect(margin, y - 5, labelWidth, height);
        pdf.rect(margin + labelWidth, y - 5, valueWidth, height);

        pdf.setFont('helvetica', 'bold');
        pdf.text(label, margin + 2, y);

        pdf.setFont('helvetica', 'normal');
        pdf.text(lines, margin + labelWidth + 2, y);

        y += height;
      };

      // ===== DATA =====
      row('Name', user?.name);

      // NEW → Clocre ID (added only)
      row('Glocre ID', user?.clocreUserId);

      row('Email', user?.email);
      row('Role', user?.role);

      row('GST Number', user?.gstNumber);
      row('Account Name', user?.BankAccountname);
      row('Account Number', user?.BankAccountnumber);
      row('Bank Name', user?.Bankname);
      row('Account Type', user?.bankAccountType);
      row('IFSC', user?.BankbranchIFSC);
      row('Branch', user?.Bankbranchname);

      const address = user?.businessAddress?.length
        ? `${user.businessAddress[0].address}, ${user.businessAddress[0].addressLine}, ${user.businessAddress[0].city}, ${user.businessAddress[0].state} - ${user.businessAddress[0].postalCode}, ${user.businessAddress[0].country}`
        : '-';

      row('Address', address);

      row('Downloaded On', new Date().toLocaleString());
      row('Downloaded By', authUser.email);

      // ===== CHEQUE PAGE =====
      if (user?.cancelledChequeDocument) {
        try {
          pdf.addPage();

          // SAME HEADER ADDED FOR PAGE 2
          pdf.addImage(logo, 'PNG', 15, 5, 40, 10);

          pdf.setFontSize(14);
          pdf.setFont('helvetica', 'bold');
          pdf.text('User Details Report', pageWidth / 2, 21, {
            align: 'center',
          });

          // existing
          pdf.text('Cheque', pageWidth / 2, 30, { align: 'center' });

          const toBase64 = url =>
            new Promise((resolve, reject) => {
              const img = new Image();

              img.crossOrigin = 'anonymous';
              img.referrerPolicy = 'no-referrer';

              img.onload = () => {
                const canvas = document.createElement('canvas');

                const MAX_WIDTH = 1800;

                let width = img.width;
                let height = img.height;

                if (width > MAX_WIDTH) {
                  height *= MAX_WIDTH / width;
                  width = MAX_WIDTH;
                }

                canvas.width = width;
                canvas.height = height;

                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);

                resolve(canvas.toDataURL('image/jpeg', 0.8));
              };

              img.onerror = reject;
              img.src = url + '?t=' + Date.now();
            });

          const base64Img = await toBase64(user.cancelledChequeDocument);

          const imgProps = pdf.getImageProperties(base64Img);

          const pdfWidth = 170;
          const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

          pdf.addImage(
            base64Img,
            'JPEG',
            20,
            40,
            pdfWidth,
            pdfHeight,
            undefined,
            'FAST'
          );
        } catch {
          console.log('Cheque skipped');
        }
      }

      // =========================
      // PAGE NUMBER FOOTER (NEW)
      // =========================
      const pageCount = pdf.getNumberOfPages();

      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);

        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');

        pdf.text(`Page ${i} of ${pageCount}`, pageWidth / 2, 290, {
          align: 'center',
        });
      }

      pdf.save(`User-${user?.name || 'details'}.pdf`);
    } catch (err) {
      console.error(err);
      toast('PDF generation failed', { type: 'error' });
    }
  };

useEffect(() => {
  if (isUserUpdated) {
    toast('User Updated Succesfully!', {
      type: 'success',
    });

    dispatch(clearUserUpdated());

    navigate('/admin/users');

    return;
  }

  if (error) {
    toast(error, {
      type: 'error',
      onOpen: () => {
        dispatch(clearError());
      },
    });
    return;
  }

  dispatch(getUser(userId));
}, [isUserUpdated, error, dispatch, navigate, userId]);

  useEffect(() => {
    if (user._id) {
      setName(user.name);
      setEmail(user.email);
      setRole(user.role);
      setGlocreMargin(user.glocreMargin ?? 5);
    }
  }, [user]);

  // Drawer
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const toggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  // Navbar
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1000);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1000);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const BASE_URL = process.env.REACT_APP_BASE_URL;

  const handleDeactivate = () => {
    const finalRemarks =
      deactivateRemarks.trim() || `Deactivated by ${authUser.email}`;

    const formData = new FormData();
    formData.append('status', 'deactivated');
    formData.append('remarks', finalRemarks);

    dispatch(updateUser(userId, formData));

    setShowDeactivateModal(false);
    setDeactivateRemarks('');
  };

  const handleActivate = () => {
    const formData = new FormData();
    formData.append('status', 'active');
    formData.append(
      'remarks',
      `Activated by ${authUser.email} on ${new Date().toLocaleString()}`
    );

    dispatch(updateUser(userId, formData));
  };

  const latestRemark = user?.remarks?.length
    ? user.remarks[user.remarks.length - 1]
    : null;

  return (
    <>
      <MetaData title={`Update User | GLOCRE`} />
      <section className="updateuser-section">
        <div className="row container-fluid">
          <div className="col-12 col-md-2">
            <Sidebar />
          </div>
          <div className="col-12 col-lg-10 col-md-12 pr-0">
            <div className="mobile-logo">
              <Link to="/">
                <img src={require('../../images/procure-g-logo.png')} />
              </Link>
            </div>
            <div className="breadcrumbWrapperr">
              {/* Breadcrumbs & Menu Icon Row (For Mobile) */}
              {isMobile ? (
                <div className="row mobile-topbar">
                  <div className="col-10">
                    <ul className="breadcrumb breadcrumb2 mb-0">
                      <li>
                        <Link to="/admin/dashboard" style={{ color: '#fff' }}>
                          Dashboard
                        </Link>
                      </li>
                      <li>
                        <Link to="/admin/users" style={{ color: '#fff' }}>
                          User List
                        </Link>
                      </li>
                      <li>Update User List</li>
                    </ul>
                  </div>
                  <div className="col-2 p-0 d-flex justify-content-center align-items-center">
                    <button className="fab" onClick={toggleDrawer}>
                      <FontAwesomeIcon icon={faList} />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="row dash-navbar-big-glc small-sticky-navbar">
                  <div className="col-lg-3 col-md-12">
                    <ul className="breadcrumb breadcrumb2 mb-0">
                      <li>
                        <Link to="/admin/dashboard">Dashboard</Link>
                      </li>
                      <li>
                        <Link to="/admin/users">User List</Link>
                      </li>
                      <li>Update User List</li>
                    </ul>
                  </div>
                  <div className="col-lg-7 col-md-6 d-flex justify-content-end align-items-end">
                    <div className="dash-cont-glc">
                      <div className="row">
                        <div className="topnav">
                          <div className="search-container">
                            <form className="d-flex">
                              <input
                                type="text"
                                placeholder="Search"
                                name="search"
                              />
                              <button type="submit">
                                <FontAwesomeIcon icon={faSearch} />
                              </button>
                            </form>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-1 col-md-2 d-flex justify-content-center align-items-end">
                    <Dropdown className="d-inline">
                      <Dropdown.Toggle
                        variant="default"
                        id="dropdown-basic"
                        className="custom-filter-toggle"
                      >
                        <FontAwesomeIcon icon={faFilter} />
                      </Dropdown.Toggle>
                    </Dropdown>
                  </div>
                </div>
              )}
              {/* Search, Filter & Avatar Row (For Mobile) */}
              {isMobile && (
                <div className="row mobile-bottombar">
                  <div className="col-9 col-md-10 pr-0">
                    <div className="search-container">
                      <form className="d-flex">
                        <input type="text" placeholder="Search" name="search" />
                        <button type="submit">
                          <FontAwesomeIcon icon={faSearch} />
                        </button>
                      </form>
                    </div>
                  </div>
                  <div className="col-3 col-md-2  d-flex justify-content-center align-items-end">
                    <Dropdown className="d-inline">
                      <Dropdown.Toggle
                        variant="default text-white"
                        id="dropdown-basic"
                        className="text-dark dropdown1 icon-list-filter-procureg"
                        style={{
                          backgroundImage: 'none',
                          border: 'none',
                          boxShadow: 'none',
                        }}
                      >
                        <FontAwesomeIcon icon={faFilter} />
                      </Dropdown.Toggle>
                    </Dropdown>
                  </div>
                </div>
              )}

              {/* Drawer Component */}
              <Drawer
                open={isDrawerOpen}
                onClose={toggleDrawer}
                direction="right"
                className="drawer"
              >
                <div className="drawer-header">
                  ADMIN DASHBOARD
                  {/* <button className="drawer-close-btn" onClick={toggleDrawer}>&times;</button> */}
                </div>
                <div className="drawer-content">
                  <ul className="drawer-links">
                    <li>
                      <Link to="/admin/dashboard">
                        <FontAwesomeIcon icon={faDashboard} /> &nbsp;Dashboard
                      </Link>
                    </li>
                    <li>
                      <Link to="/admin/products">
                        <FontAwesomeIcon icon={faCartShopping} /> &nbsp;Product
                        List
                      </Link>
                    </li>
                    <li>
                      <Link to="/admin/products/create">
                        <FontAwesomeIcon icon={faShoppingBag} /> &nbsp;Create
                        Product
                      </Link>
                    </li>
                    <li>
                      <Link to="/admin/orders">
                        <FontAwesomeIcon icon={faSort} /> &nbsp;Order List
                      </Link>
                    </li>
                    <li>
                      <Link to="/admin/users">
                        <FontAwesomeIcon icon={faUserPlus} /> &nbsp;User List
                      </Link>
                    </li>
                    <li>
                      <Link to="/admin/reviews">
                        <FontAwesomeIcon icon={faPencil} /> &nbsp;Review List
                      </Link>
                    </li>
                    <li>
                      <Link to="/admin/edit-banner">
                        <FontAwesomeIcon icon={faPencil} className="me-2" />
                        Banner
                      </Link>
                    </li>
                    <li>
                      <Link to="/admin/awsimages">
                        <FontAwesomeIcon icon={faPencil} className="me-2" />
                        Images
                      </Link>
                    </li>
                    <li>
                      <Link to="/admin/subscribers">
                        <FontAwesomeIcon icon={faPencil} className="me-2" />
                        All Emails
                      </Link>
                    </li>
                  </ul>
                </div>
              </Drawer>
            </div>

            <h3 style={{ color: '#ffad63', marginTop: '40px' }}>UPDATE USER</h3>
            <p>Glocre</p>

            <div className="update-user-glc">
              {latestRemark && (
                <div
                  style={{
                    background: '#fff3cd',
                    color: '#856404',
                    padding: '10px',
                    borderRadius: '5px',
                    marginBottom: '10px',
                    border: '1px solid #ffeeba',
                  }}
                >
                  <b>Reason:</b> {latestRemark.text} <br />
                  <b>Deactivated By:</b>{' '}
                  {latestRemark.admin
                    ? `${latestRemark.admin.name} (${latestRemark.admin.email})`
                    : 'Admin'}
                  <br />
                  <b>Date:</b>{' '}
                  {new Date(latestRemark.createdAt).toLocaleString()}
                </div>
              )}
              <form onSubmit={submitHandler} encType="multipart/form-data">
                <div className="form-group">
                  <label htmlFor="name_field">Name</label>
                  <input
                    type="text"
                    id="name_field"
                    className="form-control"
                    onChange={e => setName(e.target.value)}
                    value={name}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="price_field">Email</label>
                  <input
                    type="text"
                    id="price_field"
                    className="form-control"
                    // onChange={(e) => setEmail(e.target.value)}
                    value={email}
                  />
                </div>

                <div className="form-group custom-select-wrapper">
                  <label htmlFor="category_field">Role</label>
                  <select
                    disabled={user._id === authUser._id}
                    value={role}
                    onChange={e => setRole(e.target.value)}
                    className="form-control custom-select"
                    id="category_field"
                  >
                    <option value="admin">GPL-Admin</option>
                    <option value="user">Buyer</option>
                    <option value="seller">Seller</option>
                  </select>
                </div>

                {role === 'seller' && (
                    <div className="form-group">
                      <label htmlFor="glocre_margin">
                        Glocre Margin (%)
                      </label>
                                  
                      <input
                        type="number"
                        id="glocre_margin"
                        className="form-control"
                        min="5"
                        value={glocreMargin}
                        onChange={e => setGlocreMargin(e.target.value)}
                        required
                      />
                    </div>
                  )}


                <>
                  <p>
                    <b
                      className="info-box-p-b"
                      style={{
                        width: '110px',
                        fontSize: 'clamp(0.7rem, 2vw, 16px)',
                      }}
                    >
                      GST Number{' '}
                    </b>
                    <b
                      className="info-box-p-b2"
                      style={{ fontSize: 'clamp(0.7rem, 2vw, 16px)' }}
                    >
                      :{' '}
                    </b>
                    <span
                      className="info-box-p-span"
                      style={{ fontSize: 'clamp(0.7rem, 2vw, 16px)' }}
                    >
                      {user?.gstNumber && user.gstNumber.length > 0
                        ? `  ${user.gstNumber}`
                        : '  User has no address'}
                    </span>
                  </p>
                  <p>
                    <b
                      className="info-box-p-b"
                      style={{
                        width: '110px',
                        fontSize: 'clamp(0.7rem, 2vw, 16px)',
                      }}
                    >
                      Bank Account name{' '}
                    </b>
                    <b
                      className="info-box-p-b2"
                      style={{ fontSize: 'clamp(0.7rem, 2vw, 16px)' }}
                    >
                      :{' '}
                    </b>
                    <span
                      className="info-box-p-span"
                      style={{ fontSize: 'clamp(0.7rem, 2vw, 16px)' }}
                    >
                      {user?.BankAccountname && user.BankAccountname.length > 0
                        ? `  ${user.BankAccountname}`
                        : '  User has no address'}
                    </span>
                  </p>
                  <p>
                    <b
                      className="info-box-p-b"
                      style={{
                        width: '110px',
                        fontSize: 'clamp(0.7rem, 2vw, 16px)',
                      }}
                    >
                      Bank Account number{' '}
                    </b>
                    <b
                      className="info-box-p-b2"
                      style={{ fontSize: 'clamp(0.7rem, 2vw, 16px)' }}
                    >
                      :{' '}
                    </b>
                    <span
                      className="info-box-p-span"
                      style={{ fontSize: 'clamp(0.7rem, 2vw, 16px)' }}
                    >
                      {user?.BankAccountnumber &&
                      user.BankAccountnumber.length > 0
                        ? `  ${user.BankAccountnumber}`
                        : '  User has no address'}
                    </span>
                  </p>
                  <p>
                    <b
                      className="info-box-p-b"
                      style={{
                        width: '110px',
                        fontSize: 'clamp(0.7rem, 2vw, 16px)',
                      }}
                    >
                      Bank name{' '}
                    </b>
                    <b
                      className="info-box-p-b2"
                      style={{ fontSize: 'clamp(0.7rem, 2vw, 16px)' }}
                    >
                      :{' '}
                    </b>
                    <span
                      className="info-box-p-span"
                      style={{ fontSize: 'clamp(0.7rem, 2vw, 16px)' }}
                    >
                      {user?.Bankname && user.Bankname.length > 0
                        ? `  ${user.Bankname}`
                        : '  User has no address'}
                    </span>
                  </p>
                  <p>
                    <b
                      className="info-box-p-b"
                      style={{
                        width: '130px',
                        fontSize: 'clamp(0.7rem, 2vw, 16px)',
                      }}
                    >
                      Bank Account Type
                    </b>
                    <b
                      className="info-box-p-b2"
                      style={{ fontSize: 'clamp(0.7rem, 2vw, 16px)' }}
                    >
                      :
                    </b>
                    <span
                      className="info-box-p-span"
                      style={{ fontSize: 'clamp(0.7rem, 2vw, 16px)' }}
                    >
                      {user?.bankAccountType && user.bankAccountType.length > 0
                        ? `  ${user.bankAccountType}`
                        : '  Not specified'}
                    </span>
                  </p>
                  <p>
                    <b
                      className="info-box-p-b"
                      style={{
                        width: '110px',
                        fontSize: 'clamp(0.7rem, 2vw, 16px)',
                      }}
                    >
                      Bank branch IFSC
                    </b>
                    <b
                      className="info-box-p-b2"
                      style={{ fontSize: 'clamp(0.7rem, 2vw, 16px)' }}
                    >
                      :{' '}
                    </b>
                    <span
                      className="info-box-p-span"
                      style={{ fontSize: 'clamp(0.7rem, 2vw, 16px)' }}
                    >
                      {user?.BankbranchIFSC && user.BankbranchIFSC.length > 0
                        ? `  ${user.BankbranchIFSC}`
                        : '  User has no address'}
                    </span>
                  </p>
                  <p>
                    <b
                      className="info-box-p-b"
                      style={{
                        width: '110px',
                        fontSize: 'clamp(0.7rem, 2vw, 16px)',
                      }}
                    >
                      Bank branch name
                    </b>
                    <b
                      className="info-box-p-b2"
                      style={{ fontSize: 'clamp(0.7rem, 2vw, 16px)' }}
                    >
                      :{' '}
                    </b>
                    <span
                      className="info-box-p-span"
                      style={{ fontSize: 'clamp(0.7rem, 2vw, 16px)' }}
                    >
                      {user?.Bankbranchname && user.Bankbranchname.length > 0
                        ? `  ${user.Bankbranchname}`
                        : '  User has no address'}
                    </span>
                  </p>
                  <p>
                    <b
                      className="info-box-p-b"
                      style={{
                        width: '110px',
                        fontSize: 'clamp(0.7rem, 2vw, 16px)',
                      }}
                    >
                      Business Address
                    </b>
                    <b
                      className="info-box-p-b2"
                      style={{ fontSize: 'clamp(0.7rem, 2vw, 16px)' }}
                    >
                      :{' '}
                    </b>
                    <span
                      className="info-box-p-span"
                      style={{ fontSize: 'clamp(0.7rem, 2vw, 16px)' }}
                    >
                      {user?.businessAddress && user.businessAddress.length > 0
                        ? `${user.businessAddress[0].address}, ${user.businessAddress[0].addressLine}, ${user.businessAddress[0].city}, ${user.businessAddress[0].state} - ${user.businessAddress[0].postalCode}, ${user.businessAddress[0].country}`
                        : 'User has no address'}
                    </span>
                  </p>
                  <p>
                  <b
                    className="info-box-p-b"
                    style={{
                      width: "110px",
                      fontSize: "clamp(0.7rem, 2vw, 16px)",
                    }}
                  >
                    Cheque
                  </b>                

                  <b
                    className="info-box-p-b2"
                    style={{ fontSize: "clamp(0.7rem, 2vw, 16px)" }}
                  >
                    :{" "}
                  </b>                

                  <span
                    className="info-box-p-span"
                    style={{ fontSize: "clamp(0.7rem, 2vw, 16px)" }}
                  >
                    {user.cancelledChequeDocument || user.cancelledCheque ? (
                      (() => {
                        const file =
                          user.cancelledChequeDocument || user.cancelledCheque;               

                        const isPdf = file?.toLowerCase().endsWith(".pdf");               

                        return isPdf ? (
                        <a
                            href={file}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: "inline-block",
                              background: "#ffad63",
                              color: "#fff",
                              padding: "8px 16px",
                              borderRadius: "6px",
                              textDecoration: "none",
                              fontSize: "14px",
                              fontWeight: "500",
                            }}
                          >
                            View PDF
                          </a>
                        ) : (
                          <img
                            src={file}
                            alt="Cancelled Cheque"
                            style={{
                              width: "100%",
                              maxWidth: "300px",
                              border: "1px solid #ccc",
                            }}
                          />
                        );
                      })()
                    ) : (
                      "No cheque uploaded"
                    )}
                  </span>
                </p>
                </>
                <div style={{ display: 'flex', justifyContent: 'end' }}>
                  <button
                    type="button"
                    onClick={downloadPDF}
                    className="btn p-2 mt-3"
                    style={{ backgroundColor: '#6c757d', color: '#fff' }}
                  >
                    Download PDF
                  </button>
                  <button
                    id="login_button"
                    type="submit"
                    disabled={loading || user?.status === 'deactivated'}
                    className="btn p-2 mt-3 ms-2"
                    style={{ backgroundColor: '#ffad63', color: '#fff' }}
                  >
                    UPDATE
                  </button>
                  {user?.status !== 'deactivated' && (
                    <button
                      type="button"
                      className="btn p-2 mt-3 ms-2"
                      style={{ backgroundColor: 'red', color: '#fff' }}
                      onClick={() => setShowDeactivateModal(true)}
                    >
                      Deactivate User
                    </button>
                  )}
                  {user?.status === 'deactivated' && (
                    <button
                      type="button"
                      className="btn p-2 mt-3 ms-2"
                      style={{ backgroundColor: 'green', color: '#fff' }}
                      onClick={handleActivate}
                    >
                      Activate User
                    </button>
                  )}
                </div>
              </form>
              {showDeactivateModal && (
                <div className="modal-overlay">
                  <div className="modal-box">
                    <h4>Deactivate User</h4>

                    <textarea
                      placeholder="Enter deactivation remarks (required)"
                      value={deactivateRemarks}
                      onChange={e => setDeactivateRemarks(e.target.value)}
                      style={{
                        width: '100%',
                        height: '100px',
                        marginTop: '10px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        padding: '10px',
                      }}
                    />

                    <div
                      style={{
                        marginTop: '15px',
                        display: 'flex',
                        justifyContent: 'end',
                      }}
                    >
                      <button
                        className="btn btn-secondary me-2"
                        onClick={() => setShowDeactivateModal(false)}
                      >
                        Cancel
                      </button>

                      <button
                        className="btn btn-danger"
                        onClick={handleDeactivate}
                      >
                        Confirm Deactivate
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
